import React, { useState, useEffect } from 'react';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  Typography,
  Button,
  Container,
  Grid,
  Paper,
  Chip,
} from '@mui/material';
import Navbar from './Navbar';
import { theme } from '../../../style/theme';
import { motion, AnimatePresence } from 'framer-motion';
import ProductsSection from './ProductsSection';
import FeaturesSection from './FeaturesSection';
import VideoDemoSection from './VideoDemoSection';
import TestimonialsSection from './TestimonialsSection';
import Plans from './Plans';
import ContactSection from './ContactSection';
import Footer from './Footer';
import { Navigate, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import StorageIcon from '@mui/icons-material/Storage';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import etlDbIcon from './image-icons/etl-db-icon.png';
import etlIcon from './image-icons/etl-icon.png';
import rightIcon1 from './image-icons/right-icon1.png';
import rightIcon2 from './image-icons/right-icon2.png';
import rightIcon3 from './image-icons/right-icon3.png';
import telemetryIcon from './image-icons/telemetry-icon.png';


const MainpageContent = () => {
  const navigate = useNavigate();
  const authToken = Cookies.get('authToken');
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  if (authToken) {
    return <Navigate to="/userdashboard" replace />;
  }

  const featureCards = [
    {
      id: 0,
      label: 'ETL & ORCHESTRATION',
      title: 'Build pipelines effortlessly.',
      tags: ['SQL/dbt Support', 'DAG Orchestration', 'Auto Retries'],
      type: 'pipeline',
      metrics: [
        { label: 'Throughput', value: '1.2M/s', change: '+34%', positive: true },
        { label: 'Latency', value: '45ms', change: '-22%', positive: true },
        { label: 'Success', value: '99.9%', change: '+2%', positive: true },
      ],
    },
    {
      id: 1,
      label: 'LOGS & TELEMETRY',
      title: 'Transform logs at scale',
      tags: ['Pattern Extract', 'JSON -> Parquet', 'High-Volume'],
      type: 'logs',
      volume: '10M+ events/sec',
    },
    {
      id: 2,
      label: 'MIGRATION ENGINE',
      title: 'Migrate safely across clouds',
      tags: ['Cross-DB', 'Validated', 'Zero-Downtime'],
      type: 'migration',
      progress: 45,
      recordsMigrated: '2,182,500',
      validationStatus: 'Passed',
    },
  ];


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: '100vw',
          overflowX: 'hidden',
          bgcolor: 'background.default',
        }}
        >
        <Navbar />

        <Box
          id="hero-section"
          sx={{
            width: '100vw',
            minHeight: '100vh',
            background: 'radial-gradient(ellipse at center, rgba(15, 15, 25, 1) 0%, rgba(5, 5, 15, 1) 100%)',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            pt: { xs: 10, md: 12 },
            pb: { xs: 6, md: 8 },
            }}
        >
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 10, px: { xs: 2, sm: 3, md: 6, lg: 10 } }}>
            <Grid container spacing={{ xs: 3, sm: 4, md: 6, lg: 8 }} alignItems="center" sx={{ minHeight: { xs: 'auto', md: '80vh' } }}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    pt: { xs: 3, md: 0 },
                    pb: { xs: 3, md: 0 },
                  }}
                >
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      border: '1px solid #3454FA80',
                      borderTop: '1px solid #3454FA80',
                      borderRadius: '16px',
                      px: { xs: 1.5, sm: 1.75, md: 2 },
                      py: { xs: 0.5, sm: 0.625, md: 0.75 },
                      mb: { xs: 2, sm: 2.5, md: 3 },
                      background: '#3454FA1A',
                      }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                        fontSize: { xs: '10px', sm: '11px', md: '12px' },
                        lineHeight: '16px',
                        letterSpacing: '0.6px',
                        textTransform: 'uppercase',
                        color: '#2C59FA',
                      }}
                    >
                      HYPERMINDS DATA ENGINEERING PLATFORM
                    </Typography>
                  </Box>

                  <Typography
                    variant="h1"
                    sx={{
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 600,
                      fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem', lg: '60px' },
                      mb: { xs: 2, sm: 2.5, md: 3 },
                      color: '#ffffff',
                      lineHeight: { xs: 1.1, sm: 1.15, md: '75px' },
                      letterSpacing: '0.26px',
                    }}
                  >
                    All Your Data Flows in{' '}
                    <Box
                      component="span"
                      sx={{
                        background: 'linear-gradient(90deg, #9333EA 0%, #4F46E5 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      One
                    </Box>
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 600,
                      fontSize: { xs: '14px', sm: '16px', md: '18px' },
                      mb: { xs: 3, sm: 3.5, md: 4 },
                      color: '#FFFFFFCC',
                      lineHeight: { xs: 1.6, sm: 1.7, md: '32.5px' },
                      letterSpacing: '-0.45px',
                      maxWidth: '500px',
                    }}
                  >
                    Run ETL, pipelines, telemetry & migrations in one platform.
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => navigate("/trial")}
                      sx={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 600,
                        fontSize: { xs: '14px', sm: '15px', md: '16px' },
                        lineHeight: '24px',
                        letterSpacing: '-0.31px',
                        textAlign: 'center',
                        px: { xs: 3, sm: 3.5, md: 4 },
                        py: { xs: 1.25, sm: 1.375, md: 1.5 },
                        borderRadius: '100px',
                        background: 'linear-gradient(90deg, #9333EA 0%, #4F46E5 100%)',
                        color: '#ffffff',
                        textTransform: 'none',
                        minWidth: { xs: '140px', sm: '150px', md: '160px' },
                        boxShadow: '0 4px 20px rgba(147, 51, 234, 0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'linear-gradient(90deg, #7E22CE 0%, #4338CA 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 30px rgba(147, 51, 234, 0.4)',
                        },
                      }}
                    >
                      Start Free Trial
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => navigate("/contact")}
                      sx={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 600,
                        fontSize: { xs: '14px', sm: '15px', md: '16px' },
                        lineHeight: '24px',
                        letterSpacing: '-0.31px',
                        textAlign: 'center',
                        px: { xs: 3, sm: 3.5, md: 4 },
                        py: { xs: 1.25, sm: 1.375, md: 1.5 },
                        borderRadius: '100px',
                        color: '#ffffff',
                        borderColor: 'rgba(147, 51, 234, 0.5)',
                        backgroundColor: 'transparent',
                        textTransform: 'none',
                        minWidth: { xs: '140px', sm: '150px', md: '160px' },
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(147, 51, 234, 0.1)',
                          borderColor: 'rgba(147, 51, 234, 0.8)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      Book a Demo
                    </Button>
                  </Box>
                </motion.div>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <AnimatePresence mode="wait">
                      {featureCards.map((card, index) => {
                        if (index !== currentSlide) return null;
                        return (
                          <motion.div
                            key={card.id}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -50 }}
                            transition={{ duration: 0.5, ease: 'easeInOut' }}
                            style={{ width: '100%', height: '100%' }}
                          >
                            <Paper
                              elevation={0}
                              sx={{
                                width: '100%',
                                height: '100%',
                                background: card.type === 'logs' 
                                  ? 'linear-gradient(135deg, rgba(173, 70, 255, 0.2) 0%, rgba(173, 70, 255, 0.2) 100%)'
                                  : 'linear-gradient(135deg, rgba(43, 127, 255, 0.2) 0%, rgba(58, 80, 251, 0.2) 100%)',
                                borderRadius: '16px',
                                border: '1px solid #FFFFFF1A',
                                borderTop: '1px solid #FFFFFF1A',
                                p: { xs: 2.5, sm: 3, md: 4 },
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: '0px 25px 50px -12px #00000040',
                              }}
                            >
                            <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                              <Box sx={{ mb: { xs: 1, sm: 1.25, md: 1.5 } }}>
                                {card.type === 'pipeline' ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.25, md: 1.5 }, mb: { xs: 1.5, sm: 1.75, md: 2 } }}>
                                    <Box
                                      component="img"
                                      src={etlDbIcon}
                                      alt="ETL Database Icon"
                                        sx={{
                                          width: { xs: '44px', sm: '49px', md: '54px' },
                                          height: { xs: '44px', sm: '49px', md: '54px' },
                                          background: '#FFFFFF0D',
                                          border: '1px solid #FFFFFF1A',
                                          borderRadius: '12px',
                                          padding: { xs: '6px', sm: '7px', md: '8px' },
                                          boxSizing: 'border-box',
                                          flexShrink: 0,
                                          filter: 'drop-shadow(0 0 8px rgba(96, 165, 250, 0.6))',
                                        }}
                                      />
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.25, md: 1.5 }, flexWrap: 'wrap' }}>
                                      <Typography
                                        sx={{
                                          fontFamily: 'Sora, sans-serif',
                                          fontWeight: 600,
                                          fontSize: { xs: '11px', sm: '12px', md: '14px' },
                                          lineHeight: '16px',
                                          letterSpacing: '0.6px',
                                          textTransform: 'uppercase',
                                          color: '#60A5FA',
                                        }}
                                      >
                                        {card.label}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: { xs: '1.5rem', md: '2rem' },
                                          lineHeight: 1.2,
                                          color: '#ffffff',
                                        }}
                                      >
                                        {card.title}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ) : card.type === 'logs' ? (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, sm: 1.25, md: 1.5 }, mb: { xs: 1.5, sm: 1.75, md: 2 } }}>
                                    <Box
                                      component="img"
                                      src={telemetryIcon}
                                      alt="Telemetry Icon"
                                        sx={{
                                          width: { xs: '44px', sm: '49px', md: '54px' },
                                          height: { xs: '44px', sm: '49px', md: '54px' },
                                          background: '#FFFFFF0D',
                                          border: '1px solid #FFFFFF1A',
                                          borderRadius: '12px',
                                          padding: { xs: '6px', sm: '7px', md: '8px' },
                                          boxSizing: 'border-box',
                                          flexShrink: 0,
                                        }}
                                      />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontFamily: 'Sora, sans-serif',
                                          fontWeight: 600,
                                          fontSize: '14px',
                                          lineHeight: '16px',
                                          letterSpacing: '0.6px',
                                          textTransform: 'uppercase',
                                          color: '#BB71FB',
                                          mb: 0.5,
                                        }}
                                      >
                                        {card.label}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontFamily: 'Manrope, sans-serif',
                                          fontWeight: 500,
                                          fontSize: { xs: '18px', sm: '20px', md: '24px' },
                                          lineHeight: { xs: '24px', sm: '28px', md: '32px' },
                                          letterSpacing: '0.07px',
                                          color: '#ffffff',
                                        }}
                                      >
                                        {card.title}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ) : (
                                  <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                      {card.type === 'migration' && (
                                        <SwapHorizIcon sx={{ color: '#10B981', fontSize: { xs: 16, sm: 18, md: 20 }, filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.6))' }} />
                                      )}
                                      <Typography
                                        sx={{
                                          fontFamily: 'Sora, sans-serif',
                                          fontWeight: 600,
                                          fontSize: '14px',
                                          lineHeight: '16px',
                                          letterSpacing: '0.6px',
                                          textTransform: 'uppercase',
                                          color: '#10B981',
                                        }}
                                      >
                                        {card.label}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ mb: { xs: 1.5, sm: 1.75, md: 2 } }}>
                                      <Typography
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                                          lineHeight: 1.2,
                                          color: '#ffffff',
                    }}
                  >
                                        {card.title}
                                      </Typography>
                                    </Box>
                                  </>
                                )}
                              </Box>

                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  gap: { xs: 1, sm: 1.125, md: 1.25 }, 
                                  mb: { xs: 3, sm: 3.5, md: 4 }, 
                                  flexWrap: { xs: 'nowrap', sm: 'wrap' },
                                  overflowX: { xs: 'auto', sm: 'visible' },
                                  overflowY: 'hidden',
                                  WebkitOverflowScrolling: 'touch',
                                  scrollbarWidth: 'none',
                                  '&::-webkit-scrollbar': {
                                    display: 'none',
                                  },
                                  px: { xs: 0.5, sm: 0 },
                                  mx: { xs: -0.5, sm: 0 },
                                }}
                              >
                                {card.tags.map((tag, idx) => (
                                  <Chip
                                    key={idx}
                                    label={tag}
                                    sx={{
                                      fontFamily: 'Manrope, sans-serif',
                                      fontWeight: 500,
                                      fontSize: { xs: '11px', sm: '11.5px', md: '12px' },
                                      lineHeight: '20px',
                                      letterSpacing: '-0.15px',
                                      color: '#FFFFFFCC',
                                      background: '#FFFFFF0D',
                                      border: '1px solid #FFFFFF1A',
                                      borderRadius: '100px',
                                      height: { xs: '32px', sm: '34px', md: '36px' },
                                      width: { xs: '110px', sm: '118px', md: '125px' },
                                      padding: { xs: '6px 12px', sm: '7px 14px', md: '8px 16px' },
                                      gap: '10px',
                                      flexShrink: 0,
                                      whiteSpace: 'nowrap',
                                      '&:hover': {
                                        background: '#FFFFFF1A',
                                        borderColor: '#FFFFFF2A',
                                      },
                                      '& .MuiChip-label': {
                                        padding: 0,
                                        fontFamily: 'Manrope, sans-serif',
                                        fontWeight: 500,
                                        fontSize: { xs: '11px', sm: '11.5px', md: '12px' },
                                        lineHeight: '20px',
                                        letterSpacing: '-0.15px',
                                        color: '#FFFFFFCC',
                                      },
                                    }}
                                  />
                                ))}
                              </Box>

                              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                {card.type === 'pipeline' && (
                                  <>
                                    <Box 
                                      sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: { xs: 1, sm: 1.25, md: 1.5 }, 
                                        mb: { xs: 3, sm: 3.5, md: 4 },
                                        py: { xs: 1.5, sm: 1.75, md: 2 },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: { xs: '48px', sm: '56px', md: '64px' },
                                          height: { xs: '48px', sm: '56px', md: '64px' },
                                          borderRadius: '14px',
                                          border: '2px solid #00B8DB',
                                          background: '#00B8DB33',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          boxShadow: '0 0 12px rgba(0, 184, 219, 0.4)',
                                        }}
                                      >
                                        <Box
                                          component="img"
                                          src={etlDbIcon}
                                          alt="Database Icon"
                                          sx={{
                                            width: { xs: '18px', sm: '21px', md: '24px' },
                                            height: { xs: '18px', sm: '21px', md: '24px' },
                                          }}
                                        />
                                      </Box>
                                      
                                      <Box 
                                        sx={{ 
                                          flex: 1, 
                                          height: '2px', 
                                          background: 'linear-gradient(90deg, rgba(0, 184, 219, 0.6) 0%, rgba(0, 184, 219, 0.3) 100%)',
                                          boxShadow: '0 0 8px rgba(0, 184, 219, 0.4)',
                                        }} 
                                      />
                                      
                                      <Box
                                        sx={{
                                          width: { xs: '48px', sm: '56px', md: '64px' },
                                          height: { xs: '48px', sm: '56px', md: '64px' },
                                          borderRadius: '14px',
                                          border: '2px solid #00B8DB',
                                          background: '#00B8DB33',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          boxShadow: '0 0 12px rgba(0, 184, 219, 0.4)',
                                        }}
                                      >
                                        <Box
                                          component="img"
                                          src={etlIcon}
                                          alt="ETL Icon"
                                          sx={{
                                            width: { xs: '18px', sm: '21px', md: '24px' },
                                            height: { xs: '18px', sm: '21px', md: '24px' },
                                          }}
                                        />
                                      </Box>
                                      
                                      <Box 
                                        sx={{ 
                                          flex: 1, 
                                          height: '2px', 
                                          background: 'linear-gradient(90deg, rgba(0, 184, 219, 0.6) 0%, rgba(0, 184, 219, 0.3) 100%)',
                                          boxShadow: '0 0 8px rgba(0, 184, 219, 0.4)',
                                        }} 
                                      />
                                      
                                      <Box
                                        sx={{
                                          width: { xs: '48px', sm: '56px', md: '64px' },
                                          height: { xs: '48px', sm: '56px', md: '64px' },
                                          borderRadius: '14px',
                                          border: '2px solid #00B8DB',
                                          background: '#00B8DB33',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          boxShadow: '0 0 12px rgba(0, 184, 219, 0.4)',
                                        }}
                                      >
                                        <Box
                                          component="img"
                                          src={etlIcon}
                                          alt="ETL Icon"
                                          sx={{
                                            width: { xs: '18px', sm: '21px', md: '24px' },
                                            height: { xs: '18px', sm: '21px', md: '24px' },
                                          }}
                                        />
                                      </Box>
                                      
                                      <Box 
                                        sx={{ 
                                          flex: 1, 
                                          height: '2px', 
                                          background: 'linear-gradient(90deg, rgba(0, 184, 219, 0.6) 0%, rgba(0, 184, 219, 0.3) 100%)',
                                          boxShadow: '0 0 8px rgba(0, 184, 219, 0.4)',
                                        }} 
                                      />
                                      
                                      <Box
                                        sx={{
                                          width: { xs: '48px', sm: '56px', md: '64px' },
                                          height: { xs: '48px', sm: '56px', md: '64px' },
                                          borderRadius: '14px',
                                          border: '2px solid rgba(255, 255, 255, 0.2)',
                                          background: 'rgba(255, 255, 255, 0.05)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          opacity: 0.6,
                                        }}
                                      >
                                        <Box
                                          component="img"
                                          src={etlDbIcon}
                                          alt="Database Icon"
                                          sx={{
                                            width: { xs: '18px', sm: '21px', md: '24px' },
                                            height: { xs: '18px', sm: '21px', md: '24px' },
                                            opacity: 0.5,
                                            filter: 'grayscale(0.5)',
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    <Grid container spacing={{ xs: 1.5, sm: 1.75, md: 2 }}>
                                      {card.metrics.map((metric, idx) => {
                                        const iconMap = [rightIcon1, rightIcon2, rightIcon3];
                                        return (
                                          <Grid item xs={4} key={idx}>
                                              <Paper
                                                elevation={0}
                                                sx={{
                                                  p: { xs: 1.5, sm: 1.75, md: 2 },
                                                  background: 'rgba(255, 255, 255, 0.05)',
                                                  border: '1px solid rgba(96, 165, 250, 0.2)',
                                                  borderRadius: '10px',
                                                  textAlign: 'center',
                                                  transition: 'all 0.3s ease',
                                                '&:hover': {
                                                  background: 'rgba(255, 255, 255, 0.08)',
                                                  borderColor: 'rgba(96, 165, 250, 0.4)',
                                                  transform: 'translateY(-2px)',
                                                },
                                              }}
                                            >
                                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.5, sm: 0.625, md: 0.75 }, mb: { xs: 1, sm: 1.25, md: 1.5 } }}>
                                                <Box
                                                  component="img"
                                                  src={iconMap[idx]}
                                                  alt={metric.label}
                                                  sx={{
                                                    width: { xs: 16, sm: 18, md: 20 },
                                                    height: { xs: 16, sm: 18, md: 20 },
                                                  }}
                                                />
                                                <Typography
                                                  sx={{
                                                    fontFamily: 'Sora, sans-serif',
                                                    fontWeight: 400,
                                                    fontSize: { xs: '10px', sm: '11px', md: '12px' },
                                                    lineHeight: '16px',
                                                    letterSpacing: '0px',
                                                    color: '#99A1AF',
                                                  }}
                                                >
                                                  {metric.label}
                                                </Typography>
                                              </Box>
                                              
                                              <Typography
                                                sx={{
                                                  fontFamily: 'Sora, sans-serif',
                                                  fontWeight: 400,
                                                  fontSize: { xs: '18px', sm: '20px', md: '24px' },
                                                  lineHeight: '100%',
                                                  letterSpacing: '0.07px',
                                                  color: '#FFFFFF',
                                                  mb: 1,
                                                }}
                                              >
                                                {metric.value}
                                              </Typography>
                                              
                                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                {metric.positive ? (
                                                  <TrendingUpIcon sx={{ color: '#00D492', fontSize: { xs: 12, sm: 13, md: 14 } }} />
                                                ) : (
                                                  <TrendingDownIcon sx={{ color: '#00D492', fontSize: { xs: 12, sm: 13, md: 14 } }} />
                                                )}
                                                <Typography
                                                  sx={{
                                                    fontFamily: 'Manrope, sans-serif',
                                                    fontWeight: 400,
                                                    fontSize: { xs: '10px', sm: '11px', md: '12px' },
                                                    lineHeight: '16px',
                                                    letterSpacing: '0px',
                                                    color: '#00D492',
                                                  }}
                                                >
                                                  {metric.change}
                                                </Typography>
                                              </Box>
                                            </Paper>
                                          </Grid>
                                        );
                                      })}
                                    </Grid>
                                  </>
                                )}

                                {card.type === 'logs' && (
                                  <>
                                    <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
                                      {[100, 60, 0].map((width, idx) => (
                                        <Box
                                          key={idx}
                                          sx={{
                                            mb: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                          }}
                                        >
                                          <Box 
                                            sx={{ 
                                              flex: 1,
                                              background: '#FFFFFF0D',
                                              borderRadius: '4px',
                                              border: '1px solid #FFFFFF1A',
                                              position: 'relative',
                                              height: { xs: '28px', sm: '30px', md: '32px' },
                                              overflow: 'hidden',
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                width: `${width}%`,
                                                height: '100%',
                                                background: idx === 2 
                                                  ? 'transparent'
                                                  : 'linear-gradient(90deg, rgba(173, 70, 255, 0.3) 0%, rgba(246, 51, 154, 0.3) 100%)',
                                                borderRadius: '5px',
                                                transition: 'width 0.8s ease',
                                                boxShadow: width > 0 && idx !== 2 ? '0 0 12px rgba(173, 70, 255, 0.4)' : 'none',
                                                position: 'relative',
                                                display: 'flex',
                                                alignItems: 'center',
                                                paddingLeft: '8px',
                                              }}
                                            >
                                              <Typography
                                                sx={{
                                                  color: 'rgba(255, 255, 255, 0.7)',
                                                  fontSize: { xs: '0.6875rem', sm: '0.71875rem', md: '0.75rem' },
                                                  fontFamily: 'monospace',
                                                  whiteSpace: 'nowrap',
                                                }}
                                              >
                                                [RAW LOG DATA...]
                                              </Typography>
                                            </Box>
                                          </Box>
                                          {width === 100 && (
                                            <CheckCircleIcon
                                              sx={{ 
                                                color: '#10B981', 
                                                fontSize: { xs: 20, sm: 22, md: 24 },
                                                filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.6))',
                                              }}
                                            />
                                          )}
                                          {width !== 100 && width > 0 && (
                                            <Box
                                              sx={{
                                                width: { xs: 14, sm: 15, md: 16 },
                                                height: { xs: 14, sm: 15, md: 16 },
                                                borderRadius: '50%',
                                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                              }}
                                            />
                                          )}
                                          {width === 0 && (
                                            <Box
                                              sx={{
                                                width: { xs: 14, sm: 15, md: 16 },
                                                height: { xs: 14, sm: 15, md: 16 },
                                                borderRadius: '50%',
                                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                              }}
                                            />
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                    <Box
                                      sx={{
                                        width: { xs: '100%', md: '100%' },
                                        height: { xs: '60px', sm: '67px', md: '74px' },
                                        borderRadius: '10px',
                                        border: '1px solid #FFFFFF1A',
                                        borderTop: '1px solid #FFFFFF1A',
                                        background: '#FFFFFF0D',
                                        padding: { xs: '12px', sm: '14px', md: '17px' },
                                        paddingBottom: '1px',
                                        display: 'flex',
                                        alignItems: 'top',
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontFamily: 'Inter, sans-serif',
                                          fontWeight: 400,
                                          fontSize: { xs: '10px', sm: '11px', md: '12px' },
                                          lineHeight: '16px',
                                          letterSpacing: '0px',
                                          color: '#99A1AF',
                                          textAlign: 'left',
                                        }}
                                      >
                                        Processing Volume ({card.volume})
                                      </Typography>
                                    </Box>
                                  </>
                                )}

                                {card.type === 'migration' && (
                                  <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 2.5, md: 3 }, gap: { xs: 1, sm: 1.5, md: 2 } }}>
                                      <Box
                                        sx={{
                                          p: { xs: 1.5, sm: 1.75, md: 2 },
                                          background: 'rgba(96, 165, 250, 0.1)',
                                          borderRadius: '8px',
                                          border: '1px solid rgba(96, 165, 250, 0.3)',
                                          textAlign: 'center',
                                          flex: 1,
                                        }}
                                      >
                                        <StorageIcon sx={{ color: '#60A5FA', fontSize: { xs: 24, sm: 28, md: 32 }, mb: 1 }} />
                                        <Typography sx={{ color: '#ffffff', fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' }, fontWeight: 600 }}>
                                          Source DB
                                        </Typography>
                                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: { xs: '0.6875rem', sm: '0.71875rem', md: '0.75rem' } }}>
                                          PostgreSQL
                                        </Typography>
                                      </Box>
                                      <Box sx={{ flex: 1, mx: { xs: 1, sm: 1.5, md: 2 }, position: 'relative' }}>
                                        <Box
                                          sx={{
                        width: '100%',
                                            height: '4px',
                                            background: 'rgba(96, 165, 250, 0.2)',
                                            borderRadius: '2px',
                                            overflow: 'hidden',
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: `${card.progress}%`,
                                              height: '100%',
                                              background: 'linear-gradient(90deg, #10B981 0%, #34D399 100%)',
                                              borderRadius: '2px',
                                              transition: 'width 0.5s ease',
                                            }}
                                          />
                                        </Box>
                                        <Typography
                                          sx={{
                                            color: '#10B981',
                                            fontSize: { xs: '0.6875rem', sm: '0.71875rem', md: '0.75rem' },
                                            textAlign: 'center',
                                            mt: 0.5,
                                          }}
                                        >
                                          {card.progress}% Complete
                                        </Typography>
                                      </Box>
                                      <Box
                                        sx={{
                                          p: { xs: 1.5, sm: 1.75, md: 2 },
                                          background: 'rgba(96, 165, 250, 0.1)',
                                          borderRadius: '8px',
                                          border: '1px solid rgba(96, 165, 250, 0.3)',
                                          textAlign: 'center',
                                          flex: 1,
                                        }}
                                      >
                                        <StorageIcon sx={{ color: '#4F46E5', fontSize: { xs: 24, sm: 28, md: 32 }, mb: 1 }} />
                                        <Typography sx={{ color: '#ffffff', fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' }, fontWeight: 600 }}>
                                          Target DB
                                        </Typography>
                                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: { xs: '0.6875rem', sm: '0.71875rem', md: '0.75rem' } }}>
                                          Snowflake
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto' }}>
                                      <Box>
                                        <Typography
                                          sx={{
                                            color: 'rgba(255, 255, 255, 0.6)',
                                            fontSize: { xs: '0.6875rem', sm: '0.71875rem', md: '0.75rem' },
                                            mb: 0.5,
                                          }}
                                        >
                                          Records Migrated
                                        </Typography>
                                        <Typography
                                          sx={{
                                            color: '#ffffff',
                                            fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                                            fontWeight: 700,
                                          }}
                                        >
                                          {card.recordsMigrated}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography
                                          sx={{
                                            color: 'rgba(255, 255, 255, 0.6)',
                                            fontSize: { xs: '0.6875rem', sm: '0.71875rem', md: '0.75rem' },
                                            mb: 0.5,
                                          }}
                                        >
                                          Validation Status
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <CheckCircleIcon sx={{ color: '#10B981', fontSize: { xs: 16, sm: 18, md: 20 } }} />
                                          <Typography
                                            sx={{
                                              color: '#10B981',
                                              fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                                              fontWeight: 600,
                                            }}
                                          >
                                            {card.validationStatus}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Box>
                                  </>
                                )}
                              </Box>
                            </Box>
                          </Paper>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {[0, 1, 2].map((index) => (
                      <Box
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        sx={{
                          width: index === currentSlide ? 24 : 8,
                          height: 8,
                          borderRadius: '4px',
                          background: index === currentSlide ? '#2D58FB' : 'rgba(255, 255, 255, 0.3)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: index === currentSlide ? '0 0 12px rgba(45, 88, 251, 0.6)' : 'none',
                          '&:hover': {
                            background: index === currentSlide ? '#2D58FB' : 'rgba(255, 255, 255, 0.5)',
                            width: index === currentSlide ? 24 : 12,
                          },
                      }}
                    />
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <ProductsSection />
        <FeaturesSection />
        <VideoDemoSection />
        <TestimonialsSection />
        <Plans />
        <ContactSection />
      </Box>
      <Footer />
    </ThemeProvider>
  );
};

export default MainpageContent;
